/**
 * DocumentCard — one row in the `/documents` grid.
 *
 * Renders a MIME-appropriate icon, display_name, doc_type badge,
 * relative uploaded_at, file size, and effective_year chip (when set).
 * Actions: rename (inline), edit (modal), download, delete.
 *
 * Rename is inline so the common flow ("I typed a bad filename")
 * costs zero clicks past the pencil affordance. Notes / tags /
 * effective_year live in the edit modal because they benefit from
 * more vertical space.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  FileSpreadsheet,
  FileImage,
  FileType,
  Download,
  Trash2,
  Pencil,
  MoreHorizontal,
  Check,
  X,
  Undo2,
} from "lucide-react";
import { humanDocType } from "./UploadWidget";

/** Turn an ISO timestamp into "3 days ago". Mirrors GrantPipeline. */
export function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.round((Date.now() - then) / 1000);
  if (diff < 45) return "just now";
  if (diff < 90) return "a minute ago";
  const mins = Math.round(diff / 60);
  if (mins < 45) return `${mins} minutes ago`;
  const hours = Math.round(diff / 3600);
  if (hours < 2) return "an hour ago";
  if (hours < 24) return `${hours} hours ago`;
  const days = Math.round(diff / 86400);
  if (days < 2) return "yesterday";
  if (days < 30) return `${days} days ago`;
  const months = Math.round(diff / (86400 * 30));
  if (months < 2) return "a month ago";
  if (months < 12) return `${months} months ago`;
  const years = Math.round(diff / (86400 * 365));
  return years < 2 ? "a year ago" : `${years} years ago`;
}

export function humanBytes(n) {
  if (!Number.isFinite(n)) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function iconForMime(mime) {
  if (!mime) return FileType;
  if (mime.startsWith("image/")) return FileImage;
  if (mime === "application/pdf") return FileText;
  if (mime.includes("spreadsheet")) return FileSpreadsheet;
  if (mime.includes("wordprocessing")) return FileText;
  return FileType;
}

export default function DocumentCard({
  doc,
  onRenamed,
  onEdit,
  onDownload,
  onDelete,
  justDeleted,
  onUndo,
}) {
  const navigate = useNavigate();
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(doc.display_name || "");
  const [renameBusy, setRenameBusy] = useState(false);

  const Icon = iconForMime(doc.mime_type);

  async function saveRename() {
    if (!renameValue.trim() || renameValue === doc.display_name) {
      setRenaming(false);
      return;
    }
    setRenameBusy(true);
    try {
      await onRenamed(renameValue.trim());
      setRenaming(false);
    } finally {
      setRenameBusy(false);
    }
  }

  if (justDeleted) {
    return (
      <article
        data-testid={`documents-card-deleted-${doc.id}`}
        className="rounded-2xl p-4 flex items-center justify-between gap-3"
        style={{
          background: "rgba(148,163,184,0.04)",
          border: "1px dashed var(--v2-border)",
        }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <Trash2 className="w-4 h-4 text-v2-textMuted flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm text-v2-text truncate">
              {doc.display_name} — moved to trash.
            </p>
            <p className="text-[11px] text-v2-textMuted">
              Recoverable for {doc.recovery_days_remaining ?? 30} days from
              your account settings.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onUndo}
          data-testid={`documents-card-undo-${doc.id}`}
          className="flex-shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-semibold text-v2-background bg-v2-accent hover:bg-white transition-colors"
        >
          <Undo2 className="w-3 h-3" />
          Undo
        </button>
      </article>
    );
  }

  return (
    <article
      data-testid={`documents-card-${doc.id}`}
      className="rounded-2xl p-4 hover:-translate-y-0.5 transition-transform"
      style={{
        background: "var(--v2-surface)",
        border: "1px solid var(--v2-border)",
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div
          className="rounded-lg p-2 flex-shrink-0"
          style={{
            background: "rgba(167,139,250,0.10)",
            border: "1px solid rgba(167,139,250,0.25)",
          }}
        >
          <Icon className="w-5 h-5 text-v2-accent" />
        </div>
        <span
          data-testid={`documents-card-doc-type-${doc.id}`}
          className="text-[10px] font-semibold uppercase tracking-[0.14em] text-v2-textMuted"
        >
          {humanDocType(doc.doc_type)}
        </span>
      </div>

      <div className="mt-3">
        {renaming ? (
          <div className="flex items-center gap-1">
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              maxLength={255}
              
              autoFocus
              disabled={renameBusy}
              data-testid={`documents-card-rename-input-${doc.id}`}
              className="w-full rounded-md px-2 py-1 text-sm text-v2-text focus:outline-none focus:border-v2-accent"
              style={{
                background: "var(--v2-surface-subtle)",
                border: "1px solid var(--v2-border)",
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveRename();
                if (e.key === "Escape") {
                  setRenameValue(doc.display_name);
                  setRenaming(false);
                }
              }}
            />
            <button
              type="button"
              onClick={saveRename}
              disabled={renameBusy}
              data-testid={`documents-card-rename-save-${doc.id}`}
              className="p-1 text-v2-accent hover:text-white transition-colors"
              aria-label="Save name"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => {
                setRenameValue(doc.display_name);
                setRenaming(false);
              }}
              data-testid={`documents-card-rename-cancel-${doc.id}`}
              className="p-1 text-v2-textMuted hover:text-v2-text transition-colors"
              aria-label="Cancel rename"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => navigate(`/documents/${doc.id}`)}
            data-testid={`documents-card-title-${doc.id}`}
            className="w-full text-left text-sm font-semibold leading-snug text-v2-text hover:text-v2-accent line-clamp-2"
          >
            {doc.display_name}
          </button>
        )}
      </div>

      <div className="mt-2 flex items-center gap-2 flex-wrap text-[10px] text-v2-textMuted">
        <span data-testid={`documents-card-size-${doc.id}`}>
          {humanBytes(doc.file_size_bytes)}
        </span>
        <span>·</span>
        <span data-testid={`documents-card-uploaded-${doc.id}`}>
          Uploaded {relativeTime(doc.uploaded_at)}
        </span>
        {doc.effective_year && (
          <>
            <span>·</span>
            <span
              data-testid={`documents-card-year-${doc.id}`}
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5"
              style={{
                background: "rgba(167,139,250,0.10)",
                border: "1px solid rgba(167,139,250,0.25)",
                color: "var(--v2-accent)",
              }}
            >
              {doc.effective_year}
            </span>
          </>
        )}
      </div>

      {doc.tags && doc.tags.length > 0 && (
        <div className="mt-2 flex items-center gap-1 flex-wrap">
          {doc.tags.slice(0, 4).map((t) => (
            <span
              key={t}
              data-testid={`documents-card-tag-${doc.id}-${t}`}
              className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] text-v2-textMuted"
              style={{
                background: "rgba(148,163,184,0.10)",
                border: "1px solid rgba(148,163,184,0.20)",
              }}
            >
              #{t}
            </span>
          ))}
        </div>
      )}

      <div
        className="mt-3 pt-3 flex items-center justify-between gap-2"
        style={{ borderTop: "1px dashed var(--v2-border)" }}
      >
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setRenaming(true)}
            data-testid={`documents-card-rename-open-${doc.id}`}
            className="p-1.5 rounded-md text-v2-textMuted hover:text-v2-accent hover:bg-white/5 transition-colors"
            aria-label="Rename"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={onEdit}
            data-testid={`documents-card-edit-${doc.id}`}
            className="p-1.5 rounded-md text-v2-textMuted hover:text-v2-accent hover:bg-white/5 transition-colors"
            aria-label="Edit details"
          >
            <MoreHorizontal className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onDownload}
            data-testid={`documents-card-download-${doc.id}`}
            className="p-1.5 rounded-md text-v2-textMuted hover:text-v2-accent hover:bg-white/5 transition-colors"
            aria-label="Download"
          >
            <Download className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            data-testid={`documents-card-delete-${doc.id}`}
            className="p-1.5 rounded-md text-v2-textMuted hover:text-red-400 hover:bg-white/5 transition-colors"
            aria-label="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </article>
  );
}
